import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { billingAuthHeaders } from '../utils/billingApi';
import SubscriptionManageModal from './SubscriptionManageModal';

const planCardStyle = (selected) => ({
    textAlign: 'left',
    padding: '16px',
    borderRadius: '8px',
    border: selected ? '2px solid #7fb069' : '1px solid #e5e7eb',
    background: selected ? '#f8fdf5' : '#fff',
});

function WaivedAmount({ listCents, fmt }) {
    if (!listCents) {
        return fmt(0);
    }
    return (
        <span className="subscription-amount-trial">
            <span className="subscription-amount-struck">{fmt(listCents)}</span>
            <span className="subscription-amount-free">{fmt(0)}</span>
        </span>
    );
}

function ActivityAmount({ fee, fmt, isBetaTester }) {
    const listAmount = fee.amount_cents ?? 0;
    const chargeAmount = fee.charge_amount_cents ?? listAmount;
    const showWaived = (fee.trial_waived || chargeAmount === 0) && listAmount > 0;
    const showBetaWaived = isBetaTester && listAmount > 0;

    if (showWaived || showBetaWaived) {
        return <WaivedAmount listCents={listAmount} fmt={fmt} />;
    }
    return fmt(chargeAmount);
}

const billingApiRoot = (backendUrl) => backendUrl.replace(/\/contract\/?$/, '');

function BetaSubscriptionNotice() {
    return (
        <div className="profile-tab-panel">
            <h2>Subscription</h2>
            <p style={{ color: '#374151', lineHeight: 1.6 }}>
                You&apos;re on a complimentary beta account. You have a free access to all KeyFlo functionality.
            </p>
        </div>
    );
}

export default function ProfileSubscription({ user, backendUrl, paymentUserType }) {
    const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);
    const [manageOpen, setManageOpen] = useState(false);
    const billingBase = `${billingApiRoot(backendUrl)}/billing`;
    const knownBetaTester = paymentUserType === 'beta_tester';

    const loadSummary = useCallback(async () => {
        if (!user?.email || knownBetaTester) return;
        try {
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const res = await axios.get(
                `${billingBase}/subscription-summary?email=${encodeURIComponent(user.email)}`,
                { headers },
            );
            setSummary(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    }, [user?.email, billingBase, getAccessTokenSilently, getIdTokenClaims, knownBetaTester]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const handleSubscriptionUpdated = useCallback((data) => {
        if (data?.subscription) {
            setSummary((prev) => (prev ? { ...prev, subscription: data.subscription } : prev));
        }
        loadSummary();
    }, [loadSummary]);

    if (knownBetaTester) return <BetaSubscriptionNotice />;
    if (error && !summary) return <p style={{ color: '#b91c1c' }}>{error}</p>;
    if (!summary) return <p>Loading subscription…</p>;

    const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
    const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }) : '—');

    const isFreeTrial = summary.payment_user_type === 'free_trial';
    const isBetaTester = summary.payment_user_type === 'beta_tester';
    if (isBetaTester) return <BetaSubscriptionNotice />;
    const hasPaymentUserType = Boolean(summary.payment_user_type);
    const nextPaymentDate = summary.next_payment_at;
    const canManageSubscription = (
        (summary.payment_user_type === 'subscriber' || summary.payment_user_type === 'free_trial')
        && summary.payment_method_status === 'verified'
    );
    const cancelScheduled = Boolean(summary.subscription?.cancel_at_period_end);
    const accessEnds = summary.subscription?.current_period_end || nextPaymentDate;
    const plans = summary.available_plans || [];
    const selectedPlan = plans.find((p) => p.product_type === summary.product_type);
    const dealsRemaining = summary.free_trial_deals_remaining;
    const dealsAllowed = summary.free_trial_deals_allowed;
    const paymentVerified = summary.payment_method_status === 'verified';
    const needsSignupWizard = !isBetaTester && !paymentVerified;

    return (
        <div className="profile-tab-panel">
            <div className="subscription-header">
                <h2>Subscription</h2>
                <div className="subscription-header-actions">
                    {summary.payment_user_type === 'subscriber' && nextPaymentDate && (
                        <span className="subscription-next-payment">
                            {cancelScheduled
                                ? `Access until: ${fmtDate(accessEnds)}`
                                : `Next payment date: ${fmtDate(nextPaymentDate)}`}
                        </span>
                    )}
                    {canManageSubscription && (
                        <button
                            type="button"
                            className="subscription-manage-btn"
                            onClick={() => setManageOpen(true)}
                        >
                            Manage Subscription
                        </button>
                    )}
                </div>
            </div>

            {needsSignupWizard && (
                <p style={{ color: '#64748b', marginTop: 0, marginBottom: '20px', maxWidth: '640px', lineHeight: 1.6 }}>
                    Complete your profile and add a payment method on the Account tab before choosing a subscription plan.
                </p>
            )}

            {cancelScheduled && (
                <div className="subscription-cancel-banner">
                    Your subscription is scheduled to cancel on {fmtDate(accessEnds)}.
                </div>
            )}

            {error && <p style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</p>}

            {hasPaymentUserType && selectedPlan && (
                <>
                    <h3 style={{ marginTop: 0 }}>Your plan</h3>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: isFreeTrial && dealsAllowed != null ? '1fr 1fr' : '1fr',
                            gap: '12px',
                            marginBottom: '24px',
                            maxWidth: isFreeTrial && dealsAllowed != null ? '720px' : '520px',
                        }}
                    >
                        <div style={planCardStyle(true)}>
                            <div style={{ fontWeight: 700 }}>{selectedPlan.display_name}</div>
                            <div style={{ color: '#64748b', marginTop: '4px' }}>
                                {fmt(selectedPlan.monthly_fee_cents)}/mo · {fmt(selectedPlan.transaction_fee_cents)} per closed deal
                            </div>
                        </div>
                        {isFreeTrial && dealsAllowed != null && (
                            <div style={{ padding: '16px' }}>
                                <div style={{ fontWeight: 700 }}>
                                    {dealsRemaining > 0
                                        ? `You're on a free trial of the ${selectedPlan.display_name} plan`
                                        : 'Your free trial has ended'}
                                </div>
                                <div
                                    style={{
                                        marginTop: '6px',
                                        lineHeight: 1.5,
                                        color: dealsRemaining > 0 ? 'rgb(127, 176, 105)' : undefined,
                                    }}
                                >
                                    {dealsRemaining > 0
                                        ? `You have ${dealsRemaining} free transaction${dealsRemaining === 1 ? '' : 's'} remaining before billing starts.`
                                        : 'All of your free trial transactions have been used. New transactions will be billed at closing.'}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <h2 className="subscription-activity-heading">Subscription Activity</h2>
            <div className="subscription-section subscription-activity">
                <table className="subscription-activity-table">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th>Date</th>
                            <th className="amount-col">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.payment_user_type === 'subscriber' && summary.monthly_fee_cents > 0 && (
                            <tr>
                                <td>Monthly subscription</td>
                                <td>—</td>
                                <td className="amount-col">
                                    {isBetaTester
                                        ? <WaivedAmount listCents={summary.monthly_fee_cents} fmt={fmt} />
                                        : fmt(summary.monthly_charge_cents ?? summary.monthly_fee_cents)}
                                </td>
                            </tr>
                        )}
                        {(summary.accrued_fees || []).map((fee) => (
                            <tr key={fee.transaction_fee_id}>
                                <td>{fee.description}</td>
                                <td>{fmtDate(fee.recognition_date)}</td>
                                <td className="amount-col">
                                    <ActivityAmount fee={fee} fmt={fmt} isBetaTester={isBetaTester} />
                                </td>
                            </tr>
                        ))}
                        {summary.payment_user_type === 'subscriber' && nextPaymentDate && (
                            <tr className="subscription-activity-total">
                                <td colSpan={2}>
                                    Total to be paid on {fmtDate(nextPaymentDate)}
                                </td>
                                <td className="amount-col">{fmt(summary.projected_total_cents)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <SubscriptionManageModal
                isOpen={manageOpen}
                onClose={() => setManageOpen(false)}
                summary={summary}
                user={user}
                billingBase={billingBase}
                getAccessTokenSilently={getAccessTokenSilently}
                getIdTokenClaims={getIdTokenClaims}
                onSubscriptionUpdated={handleSubscriptionUpdated}
            />
        </div>
    );
}
